import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de fichadas (`fichadas`) para el panel de staff (Control de
 * Horarios y Liquidación de Sueldos, slice 3 — Auditoría). Habla con Supabase
 * SOLO a través del cliente anon de navegador (supabase.client.js); la
 * LECTURA la enforcea RLS (`fichadas_admin_read`, is_admin()). `fichadas` NO
 * tiene política de INSERT/UPDATE/DELETE (design.md, DDL sketch): toda
 * escritura pasa por la RPC `admin_edit_fichada` (SECURITY DEFINER,
 * migración 19) — este repositorio nunca hace `.insert()`/`.update()`/
 * `.delete()` directo sobre la tabla, solo `.select()` + la llamada RPC.
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`); `empleados(nombre)` es
 * un embed de solo lectura (mismo patrón que `mesas.repository.js`
 * `reservations(nombre, apellido)`) para no obligar a la vista a hacer un
 * segundo round-trip por cada fila.
 */

const FICHADA_COLUMNS = 'id, empleado_id, entrada_at, salida_at, origen, anulada, created_at, empleados(nombre)';

/**
 * Hard cap for `listFichadasHistorial` ("Todo el historial" — owner
 * request, 2026-09-02): a "give me everything this employee ever fichó"
 * query has no natural upper bound the way a 30-day admin range does, so a
 * long-tenured employee could otherwise pull an unbounded result set. 2000
 * rows is generous for any realistic employee history while still being a
 * real, enforced limit rather than "no limit at all" — and, since
 * review-6d2bc4aaef1ed2ce finding 2, `listFichadasHistorial` requests one
 * extra row beyond this cap to detect and report when it was actually hit.
 */
const LIST_FICHADAS_SIN_RANGO_LIMIT = 2000;

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que empleados.repository.js).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Fichadas cuyo `entrada_at` cae en `[desde, hasta]` (ambos límites
 * inclusive, timestamps ISO8601), ordenadas de más reciente a más vieja.
 * Nunca filtra por `empleado_id` en esta query — ese filtrado, cuando el
 * admin elige un empleado SIN tildar "Todo el historial", se hace
 * client-side sobre las filas ya traídas (`FichadasView.js`), no acá, porque
 * el rango por defecto ya está cargado y no hace falta un nuevo round-trip.
 * "Todo el historial" de UN empleado (sin filtro de fecha) es
 * {@link listFichadasHistorial}, una query separada — ver su propio
 * docstring (review-6d2bc4aaef1ed2ce finding 2: el split existe para que
 * ESTA query se mantenga byte-idéntica sin importar qué gane la de
 * historial).
 *
 * El rango se pasa tal cual llega — la atribución de fecha ART y el corte a
 * medianoche son responsabilidad de `js/shared/timesheet-policy.js`, no de
 * esta query.
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que
 * `listEmpleados` en empleados.repository.js).
 * @param {{ desde?: string, hasta?: string }} params
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listFichadas({ desde, hasta } = {}) {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('fichadas')
        .select(FICHADA_COLUMNS)
        .gte('entrada_at', desde)
        .lte('entrada_at', hasta)
        .order('entrada_at', { ascending: false });

    if (error) rethrow(error);
    return data ?? [];
}

/**
 * TODAS las fichadas de UN empleado, sin filtro de fecha (owner request,
 * 2026-09-02: "ver TODAS las fichadas de un empleado", checkbox "Todo el
 * historial" en `FichadasView.js`), ordenadas de más reciente a más vieja,
 * acotadas por {@link LIST_FICHADAS_SIN_RANGO_LIMIT}. Separada de
 * `listFichadas` (review-6d2bc4aaef1ed2ce finding 2 — antes era
 * `listFichadas({ sinRango: true })`) para poder señalar si el cap se
 * alcanzó sin cambiar la forma de retorno del path de rango: pide
 * `LIST_FICHADAS_SIN_RANGO_LIMIT + 1` filas y, si Supabase devuelve más que
 * el cap, recorta la respuesta a ese tope y marca `truncado: true` — la fila
 * de más (la `LIMIT + 1`-ésima) nunca se devuelve, solo prueba que había más
 * de las que se muestran. Antes de este fix el cap de 2000 era silencioso:
 * un empleado con más de 2000 fichadas veía "todo su historial" recortado
 * sin ningún indicio de que faltaban filas.
 *
 * Si Supabase no está configurado devuelve `{ fichadas: [], truncado: false
 * }` (misma degradación que `listFichadas`) — salvo que venga sin
 * `empleadoId`, que es un error de programación del caller (nunca debería
 * pasar: la UI siempre manda el id del empleado seleccionado junto con el
 * checkbox) y se rechaza ANTES de tocar Supabase, para no devolver
 * silenciosamente un resultado vacío como si fuera "sin resultados".
 * @param {{ empleadoId: string }} params
 * @returns {Promise<{ fichadas: Array<object>, truncado: boolean }>}
 * @throws {Error} si `empleadoId` no viene, o si Supabase responde con error
 */
export async function listFichadasHistorial({ empleadoId } = {}) {
    if (!empleadoId) {
        throw new Error('listFichadasHistorial: empleadoId requerido');
    }

    const client = getSupabaseClient();
    if (!client) return { fichadas: [], truncado: false };

    const { data, error } = await client
        .from('fichadas')
        .select(FICHADA_COLUMNS)
        .eq('empleado_id', empleadoId)
        .order('entrada_at', { ascending: false })
        .limit(LIST_FICHADAS_SIN_RANGO_LIMIT + 1);

    if (error) rethrow(error);

    const rows = data ?? [];
    const truncado = rows.length > LIST_FICHADAS_SIN_RANGO_LIMIT;
    return { fichadas: truncado ? rows.slice(0, LIST_FICHADAS_SIN_RANGO_LIMIT) : rows, truncado };
}

/**
 * Todas las fichadas ABIERTAS (`salida_at is null`) y no anuladas, sin
 * filtro de fecha, más viejas primero (F-B, owner decision 2026-09-27: el
 * banner del Panel Admin de jornadas abiertas hace más de 12 h). Una sola
 * query y acotada por construcción: el índice único parcial de la migración
 * 18 (`where salida_at is null and not anulada`) permite a lo sumo UNA
 * jornada abierta por empleado. El umbral de 12 h NO se filtra acá: es
 * política (`detectAnomalies`), no query.
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listFichadasAbiertas() {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('fichadas')
        .select(FICHADA_COLUMNS)
        .is('salida_at', null)
        .eq('anulada', false)
        .order('entrada_at', { ascending: true });

    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Envía un payload de creación/edición/cierre/anulación a la RPC
 * `admin_edit_fichada` (SECURITY DEFINER, migración 19). Único punto de
 * escritura de fichadas de este repositorio. Reenvía el `{ ok, ... }` que la
 * RPC ya devuelve tal cual — la traducción de códigos de error a mensajes
 * legibles vive en `admin-fichadas.service.js`, no acá.
 * @param {{ accion: 'crear'|'editar'|'cerrar'|'anular', motivo: string, fichada_id?: string, empleado_id?: string, entrada_at?: string, salida_at?: string|null }} payload
 * @returns {Promise<{ ok: boolean, error?: string, fichada_id?: string, accion?: string, cambios?: number }>}
 * @throws {Error} si Supabase no está configurado o la llamada RPC falla a
 *   nivel de transporte (permiso denegado, red, etc. — distinto de un
 *   rechazo de negocio como `motivo_requerido`, que llega en `error`)
 */
export async function editFichada(payload) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Edición no disponible');

    const { data, error } = await client.rpc('admin_edit_fichada', { p_payload: payload });

    if (error) rethrow(error);
    return data;
}
